import clsx from "clsx";

import { IMAGE_BUCKET, IMAGES_METRICS, TRANSPARENT } from "@/consts";
import {
  ImageFetchPriorityEnum,
  ImageLoadingEnum,
  ImageProviderEnum,
  ImageVariantEnum,
  ScreenWidthEnum,
} from "@/enums";
import type { CommonProps, ImageDataType } from "@/types";
import { cloudinarySource, contentfulSource } from "@/utils/helpers";

import styles from "./Image.module.css";

const ref = "image";

interface ImageProps extends ImageDataType, Pick<CommonProps, "className"> {
  fetchPriority?: ImageFetchPriorityEnum;
  loading?: ImageLoadingEnum;
  title?: string;
}

function setFetchPriority(
  fetchPriority: ImageFetchPriorityEnum | undefined,
  variant: ImageVariantEnum,
) {
  if ([ImageVariantEnum.HERO, ImageVariantEnum.PRIMARY].includes(variant)) {
    return ImageFetchPriorityEnum.HIGH;
  }

  return fetchPriority || ImageFetchPriorityEnum.LOW;
}

function setLoading(
  loading: ImageLoadingEnum | undefined,
  variant: ImageVariantEnum,
) {
  if (variant === ImageVariantEnum.HERO) {
    return ImageLoadingEnum.EAGER;
  }

  return loading || ImageLoadingEnum.LAZY;
}

function setSrc(
  provider: ImageProviderEnum,
  src: string,
  variant: ImageVariantEnum = ImageVariantEnum.NONE,
) {
  if (provider === ImageProviderEnum.BUCKET) {
    return src.startsWith("https://") ? src : `${IMAGE_BUCKET}/${src}`;
  }

  const metrics = IMAGES_METRICS[variant][ScreenWidthEnum.BASE];

  if (!metrics) {
    return src;
  }

  const source = (
    provider === ImageProviderEnum.CLOUDINARY
      ? cloudinarySource(src, 0, metrics)
      : contentfulSource(src, 0, metrics)
  ).pop();

  return source ? source.srcset : src;
}

function useImage(props: ImageProps) {
  const className = clsx(styles[ref], props.className);
  const {
    alt = "",
    fetchPriority,
    loading,
    provider = ImageProviderEnum.BUCKET,
    src = TRANSPARENT,
    variant = ImageVariantEnum.NONE,
  } = props;

  return {
    alt,
    className,
    fetchPriority: setFetchPriority(fetchPriority, variant),
    loading: setLoading(loading, variant),
    provider,
    src,
    variant,
  };
}

function Image(props: ImageProps) {
  const { alt, className, fetchPriority, loading, provider, src, variant } =
    useImage(props);

  if (!src) {
    return null;
  }

  const imgSrc = setSrc(provider, src, variant);

  return (
    <img
      alt={alt}
      className={className}
      decoding="async"
      fetchPriority={
        fetchPriority === ImageFetchPriorityEnum.AUTO
          ? undefined
          : fetchPriority
      }
      height={1}
      loading={loading === ImageLoadingEnum.EAGER ? undefined : loading}
      src={TRANSPARENT}
      srcSet={imgSrc}
      title={props.title}
      width={1}
      data-type={
        variant === ImageVariantEnum.NONE ||
        provider === ImageProviderEnum.BUCKET
          ? undefined
          : variant
      }
    />
  );
}

export default Image;
export { Image, type ImageProps };
